import { StoryblokComponent } from 'storyblok-js-client';
import { ShortTextProps } from '@fc/angie-ui/src/components/ShortText/types';
import { LinkButtonProps } from '@fc/angie-ui/src/components/LinkButton/types';
import { Store } from 'vuex';
import { MediaBackgroundProps, Position, BGColor } from '~/components/storyblok/MediaBackground/types';
import { IUniqueProps } from '~/components/storyblok/Unique/types';
import { getProduct } from '~/utils/accessoryBundleProducts';
import { FormattedProduct } from '@fc/app-common/src/services/Shopify/types';
import { ACCESSORY_TYPE, PACKAGE_TYPE } from '~/constants/storyblok';
import {
  ICmsFCAccessory, IFCAccessoryProperties, ICmsFCPackage, IFCPackageProperties,
  ISelectorFCAccessory, ISelectorFCPackage, TProductType, IStoryblokPictureResponsive,
  IStoryblokVideo, IStoryblokShortText,
} from './types';

export interface IStoryblokLinkButton extends StoryblokComponent<string> {
  ctaText: string;
  ctaLink: {
    cached_url: string;
    linktype: string;
  },
  codeId: string;
  buttonTheme: string;
  buttonTextSize: string;
  useHeadingFont: boolean;
  buttonAlignment: string;
  fullWidth: boolean;
  mobileAlignmentCenter: boolean;
}

export function transformLinkButtonContent(contentObj: IStoryblokLinkButton): LinkButtonProps {
  const elementName = (contentObj?.ctaLink?.linktype === 'story' || !contentObj?.ctaLink?.cached_url.includes('http')) ? 'NuxtLink' : 'a';
  return {
    ctaText: contentObj?.ctaText,
    ctaLink: contentObj?.ctaLink?.cached_url,
    buttonTheme: contentObj?.buttonTheme,
    buttonSize: contentObj?.buttonTextSize,
    useHeadingFont: contentObj?.useHeadingFont,
    buttonAlignment: contentObj?.buttonAlignment,
    fullWidth: contentObj?.fullWidth,
    elementName,
    'data-testid': contentObj?.codeId ?? 'atf-button',
    mobileAlignmentCenter: contentObj?.mobileAlignmentCenter ?? false,
  };
}

const handleStringArray = (content: any) => {
  const classes = (Array.isArray(content) ? content.join(' ') : content);
  return classes;
};

export function transformShortTextContent(contentObj: IStoryblokShortText): ShortTextProps {
  return {
    text: contentObj?.text,
    textTheme: contentObj?.textTheme,
    elementName: contentObj?.elementName,
    textColor: contentObj?.textColor,
    isBold: contentObj?.isBold,
    textAlignment: handleStringArray(contentObj?.textAlignment),
    isUpperCase: contentObj?.isUpperCase,
    maxWidth: contentObj?.maxWidth,
    id: contentObj?.id,
    paddingProperties: {
      paddingTop: contentObj?.paddingTop,
      paddingBottom: contentObj?.paddingBottom,
      paddingLeft: contentObj?.paddingLeft,
      paddingRight: contentObj?.paddingRight,
    },
  };
}

export function transformPictureResponsiveContent(contentObj: IStoryblokPictureResponsive): { src: string, alt: string, } {
  return {
    src: contentObj?.Image.filename,
    alt: contentObj?.Image.alt,
  };
}

export function transformPictureResponseFCImageContent(contentObj: IStoryblokPictureResponsive) {
  return {
    component: 'FCImage',
    nuxtImageProps: {
      src: contentObj?.Image.filename,
      alt: contentObj?.Image.alt,
    },
    rounded: contentObj?.rounded,
    displayType: contentObj?.imageType,
    fcProvider: 'storyblok',
  };
}

export interface IStoryblokMediaBackground extends StoryblokComponent<string> {
  mediaContainer: [IStoryblokPictureResponsive, IStoryblokVideo],
  mediaPosition: Position;
  backgroundColor: BGColor;
  isFullWidth: boolean;
  brightness: number;
}

export function transformMediaBackgroundContent(contentObj: IStoryblokMediaBackground): MediaBackgroundProps {
  const videoComponent = contentObj?.mediaContainer.find(block => block.component === 'Video') as IStoryblokVideo;
  const pictureResponsiveComponent = contentObj?.mediaContainer.find(block => block.component === 'PictureResponsive') as IStoryblokPictureResponsive;

  return {
    imageSrc: pictureResponsiveComponent?.Image?.filename || '',
    imageAlt: pictureResponsiveComponent?.Image?.alt || '',
    firstFrameImageUrl: videoComponent?.firstFrameImage?.filename || '',
    webmVideo: videoComponent?.webmVideoUrl || '',
    mp4Video: videoComponent?.mp4VideoUrl || '',
    webmMobileVideo: videoComponent?.webmMobileVideoUrl || '',
    mp4MobileVideo: videoComponent?.mp4MobileVideoUrl || '',
    mobileVideoPlayback: videoComponent?.mobileVideoPlayback || 'auto',
    tabletUpVideoPlayback: videoComponent?.tabletUpVideoPlayback || 'auto',
    mediaPosition: Position[contentObj?.mediaPosition] || Position.center,
    backgroundColor: contentObj?.backgroundColor || BGColor.white,
    isFullWidth: contentObj?.isFullWidth || true,
    videoComponent,
    pictureResponsiveComponent,
    brightness: contentObj.brightness,
  };
}

export function transformUniqueContent(props: IUniqueProps) {
  return {
    ...props,
  };
}

export interface IStoryblokCarousel extends StoryblokComponent<string> {
  slides: any[];
}

export function transformCarouselContent(component: IStoryblokCarousel) {
  return {
    ...component,
  };
}

// TODO moves to fightcamp.ts https://github.com/fightcamp/fightcamp-web-monorepo/pull/949#discussion_r1538097065
export interface IStore {
  getters: {
    productsCollection: any[];
  };
}

interface IShopifyVariant {
  id: string;
  sku: string;
  title: string;
}

interface IShopifyProduct {
  id: string;
  sku: string | false;
  name: string;
  type: string;
  handle: string;
  description: string;
  variants: IShopifyVariant[];
}

/*
  this handles the logic when Storyblok returns a FCProducts component which contains
  a Shopify test plugin and a Shopify product plugin

  This method is used for package products like trackers and trackers + bag since hardcoded data varies for each product and
  many Storyblok components have hardcoded data for these packages as well
*/
export function transformFcProductContent(component: any, store: Store<IStore>) {
  const items = process.env.NUXT_ENV_IS_DEV === 'true' ? component?.shopifyTest?.items : component?.shopifyProd?.items;

  if (!store.getters.productsCollection) {
    return [];
  }

  return items
    .map((product: IShopifyProduct) => store.getters.productsCollection.find((item: IShopifyProduct) => item.id === product.id))
    .filter((item: IShopifyProduct) => item !== undefined);
}

interface ITransfromShopifyProdIdParams {
  testShopifyId: string;
  prodShopifyId: string;
  oldId: string;
  store: Store<IStore>;
  selectsData?: {
    name: string;
    hint: string;
  };
}

/*
  this handles the logic when Storyblok returns a component that contains testShopifyId and
  prodShopifyId values

  This method is used for accessory/bundle products since they are more stand alone in our code and storyblok
*/
export function transformShopifyProdId({
  testShopifyId, prodShopifyId, oldId, store, selectsData = { name: '', hint: '' },
}: ITransfromShopifyProdIdParams) {
  const id = process.env.NUXT_ENV_IS_DEV === 'true' ? testShopifyId : prodShopifyId;
  const ecommData = store.getters.productsCollection.find((item: any) => item.id === id);

  // if item is found in Shopify return transformed item data if not return undefined
  if (ecommData) {
    const hardCodeData: any = getProduct(oldId) ?? {};
    let selects = null;

    // Create selects object if hardcoded data exists or we have multiple variants
    if (hardCodeData.selects) {
      selects = {
        name: hardCodeData.selects.name,
        key: hardCodeData.selects.key,
        hint: hardCodeData.selects.hint,
        options: ecommData.variants || [],
      };
    } else if (ecommData.variants?.length > 1) {
      selects = {
        name: selectsData.name,
        key: ecommData.id,
        hint: selectsData.hint,
        options: ecommData.variants || [],
      };
    }

    let types = [];
    // Try to parse productFilter metadata from Shopify or use hardcoded types
    try {
      if (ecommData?.metadata?.productFilter) {
        types = JSON.parse(ecommData.metadata.productFilter);
      } else if (hardCodeData?.types) {
        types = hardCodeData.types;
      }
    } catch {
      types = hardCodeData?.types ?? [];
    }

    return {
      ...hardCodeData,
      id: ecommData.id,
      title: ecommData.title,
      oldPrice: ecommData.variants[0].compareAtPrice,
      imgUrl: ecommData.image,
      secondaryImgUrl: ecommData?.images?.[1] ?? undefined,
      price: ecommData.variants[0].price,
      variants: ecommData.variants,
      selects,
      types,
    };
  }

  return undefined;
}

/*
  Main takeaway from formatting Shopify data with our hard coded data is that our product data structure are not consistent
  Components are reading product data from different sources
  We need to have one source of truth and data structure that all products will follow
*/
export function transformShopifyProductForAnalytics({ product, variantId }: {product: FormattedProduct, variantId: string}) {
  return {
    id: product.id,
    productName: product.title,
    productType: product.productType,
    price: product.variants[0].price,
    pathId: '', // TODO we use to hardcode this, can we create it programmatically?
    primaryImg: {
      src: product.image,
      alt: '',
    },
    variantId,
  };
}

function selectAssetsToImageOrVideo(asset: IStoryblokPictureResponsive | IStoryblokVideo): { src: string; alt: string } | IStoryblokVideo | {} {
  if (asset.component === 'PictureResponsive') {
    return { src: asset?.Image?.filename, alt: asset?.Image?.alt ?? '' };
  } if (asset.component === 'Video') {
    // TODO transform data
    return asset;
  }
  return {};
}

function getAssets(assets: (IStoryblokPictureResponsive | IStoryblokVideo)[]) {
  return Array.isArray(assets)
    ? assets
      .filter(asset => asset !== undefined)
      .map((asset: IStoryblokPictureResponsive | IStoryblokVideo) => selectAssetsToImageOrVideo(asset))
      .filter((asset): asset is { src: string; alt: string } | IStoryblokVideo => asset !== null)
    : [];
}

function filterNonAccessoryItems(item: any): boolean {
  return item?.content?.productTypeData[0]?.component === ACCESSORY_TYPE;
}

function selectFcPackage({ content }: { content: ICmsFCPackage }): IFCPackageProperties {
  // there are never more than one item in these arrays
  const includedItems = content?.includes?.[0]?.ecommerceProducts ?? [];
  const warranties = content?.warranties?.[0]?.ecommerceProducts ?? [];
  const gloveUpsells = content?.gloveUpsells?.[0]?.ecommerceProducts ?? [];
  const upsells = content?.upsells?.[0]?.ecommerceProducts ?? [];

  return {
    includes: includedItems.length ? includedItems.filter(filterNonAccessoryItems).map(selectFCBaseProduct) as ISelectorFCAccessory[] : [],
    gloveUpsells: gloveUpsells.length ? gloveUpsells.filter(filterNonAccessoryItems).map(selectFCBaseProduct) as ISelectorFCAccessory[] : [],
    upsells: upsells.length ? upsells.filter(filterNonAccessoryItems).map(selectFCBaseProduct) as ISelectorFCAccessory[] : [],
    warranties: warranties.length ? warranties.filter(filterNonAccessoryItems).map(selectFCBaseProduct) as ISelectorFCAccessory[] : [],
    includesBag: content?.includesBag ?? false,
    assets: content?.assets ? getAssets(content.assets) : [],
  };
}

// TODO provide a default image if not present for all and fix type errors
function selectFCAccessory({ content }: { content: ICmsFCAccessory }): IFCAccessoryProperties {
  return {
    lifestyleImage: {
      src: content?.lifestyleImage?.filename ?? '',
      alt: content?.lifestyleImage?.alt ?? '',
    },
    includesModalCopy: content?.includesModalCopy ?? null,
    assets: content?.assets ? getAssets(content.assets) : [],
  };
}

const getProductTypeMethod = new Map<TProductType, Function>([
  [PACKAGE_TYPE, selectFcPackage],
  [ACCESSORY_TYPE, selectFCAccessory],
  // [BUNDLE_TYPE, transformFCBundle],
]);

function findAdditionalProductTypeData({ type, content }: { type: TProductType; content: ICmsFCPackage | ICmsFCAccessory }): IFCPackageProperties | IFCAccessoryProperties | {} {
  const productTypeMethod = getProductTypeMethod.get(type);
  const result = productTypeMethod ? productTypeMethod({ content }) : {};

  return result;
}

export function selectFCBaseProduct(product: ICmsFCPackage | ICmsFCAccessory): ISelectorFCPackage | ISelectorFCAccessory {
  const prodId = product?.content?.shopifyIds?.[0]?.shopifyProd?.items?.[0]?.id || '';
  const devId = product?.content?.shopifyIds?.[0]?.shopifyTest?.items?.[0]?.id || '';
  const id = process.env.NUXT_ENV_IS_PROD === 'true' ? prodId : devId;

  return {
    title: product?.content?.title ?? '',
    pathId: product?.name ?? '',
    description: product?.content?.description ?? '',
    shortDescription: product?.content?.shortDescription ?? '',
    isSoldOut: product?.content?.isSoldOut ?? false,
    eCommId: id,
    primaryImg: {
      src: product?.content?.image?.filename ?? '',
      alt: product?.content?.image?.alt ?? '',
    },
    ...findAdditionalProductTypeData({ type: product?.content?.productTypeData[0]?.component, content: product?.content?.productTypeData[0] }),
  } as ISelectorFCPackage | ISelectorFCAccessory;
}
